import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for stable search that prevents race conditions.
 *
 * Problem: When typing quickly, earlier API responses can arrive AFTER
 * later responses, showing stale results.
 *
 * Solution (defense-in-depth, 2026-05):
 *   1. Track request ID (incremented for each search) — only the latest
 *      response can update state.
 *   2. AbortController cancels the prior request when a new one starts.
 *   3. At response time, also compare the query we kicked off against
 *      `lastQueryRef`. If they differ, drop the result.
 *
 * Earlier versions added a 2.5s "verification" re-fetch as a safety net
 * for races. That doubled backend load per keystroke without adding
 * safety on top of the 3 guards above, so it was removed in 2026-05.
 *
 * @param {Function} searchFn - Async function that performs the search
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300)
 */
export const useStableSearch = (searchFn, debounceMs = 300) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  // Request tracking refs
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastQueryRef = useRef('');
  const lastResultQueryRef = useRef('');

  // Perform the actual search with race condition protection
  const executeSearch = useCallback(async (searchQuery) => {
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      setResults([]);
      setIsSearching(false);
      setError(null);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    lastQueryRef.current = trimmedQuery;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsSearching(true);
    setError(null);

    try {
      const searchResults = await searchFn(trimmedQuery, abortController.signal);
      // Guard 1: only the latest fetch wins.
      if (currentRequestId !== requestIdRef.current) return;
      // Guard 2: the query we kicked off still matches what's typed.
      if (lastQueryRef.current !== trimmedQuery) return;

      setResults(searchResults || []);
      lastResultQueryRef.current = trimmedQuery;
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      if (currentRequestId === requestIdRef.current) {
        console.error('[StableSearch] Search error:', err);
        setError(err);
        setResults([]);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsSearching(false);
      }
    }
  }, [searchFn]);

  // Debounced search handler
  const handleSearch = useCallback((newQuery) => {
    setQuery(newQuery);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (!newQuery.trim()) {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      setResults([]);
      setIsSearching(false);
      lastResultQueryRef.current = '';
      return;
    }
    debounceTimerRef.current = setTimeout(() => {
      executeSearch(newQuery);
    }, debounceMs);
  }, [executeSearch, debounceMs]);

  // Clear search completely
  const clearSearch = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setQuery('');
    setResults([]);
    setIsSearching(false);
    setError(null);
    lastQueryRef.current = '';
    lastResultQueryRef.current = '';
    requestIdRef.current++;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  return {
    query,
    results,
    isSearching,
    error,
    setQuery: handleSearch,
    clearSearch,
    setQueryDirect: setQuery,
  };
};

/**
 * Factory function for stable search (non-hook version).
 * Same three guards as the hook above; no 2.5s verification re-fetch.
 */
export const makeStableSearch = (debounceMs = 300) => {
  let requestId = 0;
  let abortController = null;
  let debounceTimer = null;
  let lastQuery = '';

  return (query, searchFn, setResults, setLoading) => {
    if (debounceTimer) clearTimeout(debounceTimer);

    const trimmed = (query || '').trim();
    if (!trimmed) {
      if (abortController) abortController.abort();
      setResults([]);
      if (setLoading) setLoading(false);
      lastQuery = '';
      return;
    }

    if (setLoading) setLoading(true);
    lastQuery = trimmed;

    const doSearch = async () => {
      const currentId = ++requestId;
      if (abortController) abortController.abort();
      const ac = new AbortController();
      abortController = ac;

      try {
        const results = await searchFn(trimmed, ac.signal);
        if (currentId !== requestId) return;
        if (lastQuery !== trimmed) return;
        setResults(results || []);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        if (currentId === requestId) setResults([]);
      } finally {
        if (currentId === requestId && setLoading) setLoading(false);
      }
    };

    debounceTimer = setTimeout(doSearch, debounceMs);
  };
};

export const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default useStableSearch;
